import React from 'react';

import { Modal, Button } from '@/components/ui';
import { useIsMobile } from '@/lib/hooks';
import { useNotifications } from '@/stores/notifications';

import { useRequestTimezoneChange } from '../../api/queries';

export interface TimezoneConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TimezoneConfirmationModal = React.forwardRef<
  HTMLDivElement,
  TimezoneConfirmationModalProps
>(({ isOpen, onClose }, ref) => {
  const isMobile = useIsMobile();
  const addNotification = useNotifications(state => state.addNotification);

  const requestTimezoneChangeMutation = useRequestTimezoneChange({
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Ticket created successfully!',
        message: 'Our team will get in touch with you shortly.',
      });
      onClose();
    },
    onError: (error: Error) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to create timezone change request. Please try again.';
      addNotification({
        type: 'error',
        title: 'Failed to create ticket',
        message: errorMessage,
      });
    },
  });

  const handleCreateTicket = () => {
    // API call with empty body - no parameters needed
    requestTimezoneChangeMutation.mutate();
  };

  return (
    <Modal
      ref={ref}
      isOpen={isOpen}
      onClose={onClose}
      title="Timezone Change Confirmation"
      hideTitle={true}
      description="Are you sure you want to change the timezone?"
      hideDescription={true}
      className="p-6 max-w-sm md:max-w-md rounded-2xl mx-auto max-h-unset"
    >
      <div className="flex flex-col gap-4 items-center justify-center">
        {/* Title */}
        <div className="w-full">
          <h2 className="font-bold text-xl leading-7 text-center text-magical-moonlight-d3">
            Are you sure you want to change the timezone?
          </h2>
        </div>

        {/* Description */}
        <div className="w-full">
          <p className="text-sm leading-5 text-center text-magical-moonlight-d2">
            Confirm here and we will create a support ticket for you, our team
            will get in touch with you shortly to help you further.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-center gap-4 w-full">
          <Button
            variant="outline"
            size={isMobile ? 'md' : 'lg'}
            onClick={onClose}
            disabled={requestTimezoneChangeMutation.isPending}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            size={isMobile ? 'md' : 'lg'}
            onClick={handleCreateTicket}
            disabled={requestTimezoneChangeMutation.isPending}
            isLoading={requestTimezoneChangeMutation.isPending}
          >
            Create a ticket
          </Button>
        </div>
      </div>
    </Modal>
  );
});

TimezoneConfirmationModal.displayName = 'TimezoneConfirmationModal';

export { TimezoneConfirmationModal };
export default TimezoneConfirmationModal;
